import { Linking, Platform, Share } from 'react-native';

import { facebookConfig } from '@/src/config/env';
import {
  buildFacebookShareUrl as buildFacebookShareUrlWithAppId,
  type FacebookShareDisplayMode,
  shouldUseSameWindowExternalShare,
} from '@/src/features/sharing/share-link-url';
import { copyTextToClipboard } from '@/src/utils/clipboard';
import { isShareCancelError } from '@/src/utils/errors';

export type SharedLinkMethod = 'clipboard' | 'facebook' | 'native' | 'web';

export {
  shouldUseSameWindowExternalShare,
} from '@/src/features/sharing/share-link-url';

export type ShareLinkResult =
  | { cancelled: false; method: Exclude<SharedLinkMethod, 'facebook'> }
  | { cancelled: true; method: null };

function getWebExternalShareContext() {
  const nav =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { standalone?: boolean })
      : null;
  const displayModeStandalone =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;

  return {
    displayModeStandalone,
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    platformOS: Platform.OS,
    standalone: nav?.standalone === true,
    userAgent: nav?.userAgent ?? '',
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : undefined,
  };
}

function getCurrentWebUrl() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null;
  }

  return window.location.href;
}

export function buildFacebookShareUrl({
  display,
  redirectUrl,
  url,
}: {
  display?: FacebookShareDisplayMode;
  redirectUrl?: string;
  url: string;
}) {
  return buildFacebookShareUrlWithAppId({
    appId: facebookConfig.appId,
    display,
    redirectUrl,
    url,
  });
}

export async function openExternalShareUrl(
  url: string,
  options: { target?: 'new-tab' | 'same-window' } = {},
) {
  if (
    options.target === 'same-window' &&
    Platform.OS === 'web' &&
    typeof window !== 'undefined'
  ) {
    window.location.assign(url);
    return;
  }

  if (
    Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    typeof window.open === 'function'
  ) {
    const popup = window.open(
      url,
      '_blank',
      'noopener,noreferrer,width=640,height=720',
    );

    if (popup) {
      popup.opener = null;
      return;
    }
  }

  await Linking.openURL(url);
}

export async function openFacebookShareDialog(url: string) {
  const sameWindow = shouldUseSameWindowExternalShare(getWebExternalShareContext());
  const shareUrl = buildFacebookShareUrl({
    display: sameWindow ? 'touch' : 'popup',
    redirectUrl: sameWindow ? (getCurrentWebUrl() ?? url) : url,
    url,
  });

  await openExternalShareUrl(shareUrl, {
    target: sameWindow ? 'same-window' : 'new-tab',
  });
}

export async function shareLinkWithDevice({
  text,
  title,
  url,
}: {
  text: string;
  title: string;
  url: string;
}): Promise<ShareLinkResult> {
  if (
    Platform.OS === 'web' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function'
  ) {
    try {
      await navigator.share({ text, title, url });
      return { cancelled: false, method: 'web' };
    } catch (error) {
      if (isShareCancelError(error)) {
        return { cancelled: true, method: null };
      }

      await copyTextToClipboard(url);
      return { cancelled: false, method: 'clipboard' };
    }
  }

  if (Platform.OS === 'web') {
    await copyTextToClipboard(url);
    return { cancelled: false, method: 'clipboard' };
  }

  const shareResult = await Share.share({
    message: url,
    title,
    url,
  });

  if (shareResult.action === Share.dismissedAction) {
    return { cancelled: true, method: null };
  }

  return { cancelled: false, method: 'native' };
}
